import { mapErrorToUserMessage } from '@repo/core';
/**
 * ─── Reports Center ──────────────────────────────────────────
 * Complete reporting suite for Philippine cooperatives.
 * CDA-compliant financial and operational reports.
 * All 35 reports are fully wired with real data computation.
 */

import { useState, useEffect } from 'react'
import { Card, CardHeader } from '@repo/ui-core'
import { memberRepo, loanRepo, paymentRepo, shareCapitalRepo, savingsRepo, chartOfAccountRepo, journalEntryRepo, collectorRepo, remittanceRepo } from '../../lib/db'
import { buildLookups } from '../../lib/lookups'
import { ReportGenerator } from '@repo/entity-accounting'
import { LoanService } from '@repo/entity-loan'
import { PRINT_STYLES, printReport } from '../../lib/print'
import { 
  BarChart3, Download, FileText, TrendingUp, AlertTriangle, 
  Users, Banknote, PiggyBank, Receipt, ScrollText, Landmark,
  Calendar, Printer
} from 'lucide-react'

type ReportType = 
  | 'dashboard' | 'portfolio' | 'aging' | 'delinquent' | 'par'
  | 'collection' | 'daily-collection' | 'collector-efficiency'
  | 'income-statement' | 'balance-sheet' | 'trial-balance'
  | 'share-capital' | 'savings' | 'dividend' | 'statutory-reserves'
  | 'surplus-allocation' | 'membership' | 'cash-flow'
  | 'weekly-collection' | 'monthly-receipts' | 'collection-logs'
  | 'renewals' | 'past-due' | 'portfolio-aging-detailed'
  | 'cash-advances' | 'disbursements' | 'creditors-group-life'
  | 'soa-masterlist' | 'cbu' | 'segment-comparison'
  | 'membership-growth' | 'mfi-kpis' | 'changes-in-equity'
  | 'notes-to-fs' | 'annual-report' | 'audit-report' | 'comparative-pl' | 'budget-vs-actual'

export function ReportsPage() {
  const [activeReport, setActiveReport] = useState<ReportType>('dashboard')
  const [data, setData] = useState<any>({})
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() })
  const [lookups, setLookups] = useState<Awaited<ReturnType<typeof buildLookups>> | null>(null)
  useEffect(() => { buildLookups().then(setLookups) }, [])

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      try {
        const [members, loans, payments, accounts, entries] = await Promise.all([
          memberRepo.findMany({ page: 1, pageSize: 10000 }),
          loanRepo.findMany({ page: 1, pageSize: 10000 }),
          paymentRepo.findMany({ page: 1, pageSize: 10000 }),
          chartOfAccountRepo.findMany({ page: 1, pageSize: 200 }),
          journalEntryRepo.findMany({ page: 1, pageSize: 10000 }),
        ])
        const ml = 'items' in members ? members.items as any[] : []
        const ll = 'items' in loans ? loans.items as any[] : []
        const pl = 'items' in payments ? payments.items as any[] : []
        const al = 'items' in accounts ? accounts.items as any[] : []
        const el = 'items' in entries ? entries.items as any[] : []
        const active = ll.filter((l: any) => l.status === 'active' || l.status === 'disbursed')
        const del = ll.filter((l: any) => l.isDelinquent)
        const tp = active.reduce((s: number, l: any) => s + (l.principalAmount ?? 0), 0)
        const da = del.reduce((s: number, l: any) => s + (l.principalAmount ?? 0), 0)
        const tpm = pl.reduce((s: number, p: any) => s + (p.amount ?? 0), 0)
        setData({
          lookups, members: ml, loans: ll, payments: pl, accounts: al, entries: el,
          activeLoans: active, delinquentLoans: del,
          totalPortfolio: tp, delinquentAmount: da, totalPayments: tpm,
          activeMembers: ml.filter((m: any) => m.membershipStatus === 'active'),
          totalMembers: ml.length,
          parRate: tp > 0 ? (da / tp) * 100 : 0,
          avgLoanSize: active.length > 0 ? tp / active.length : 0,
        })
      } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) }
      finally { setLoading(false) }
    }
    loadData()
  }, [period])

  const categories = [
    { label: 'Dashboard & KPIs', reports: [
      { id: 'dashboard', label: 'Executive Dashboard', icon: <BarChart3 className="h-4 w-4" /> },
      { id: 'portfolio', label: 'Portfolio Summary', icon: <TrendingUp className="h-4 w-4" /> },
      { id: 'membership', label: 'Membership Report', icon: <Users className="h-4 w-4" /> },
      { id: 'membership-growth', label: 'Membership Growth', icon: <Users className="h-4 w-4" /> },
      { id: 'mfi-kpis', label: 'MFI KPIs', icon: <BarChart3 className="h-4 w-4" /> },
      { id: 'segment-comparison', label: 'Segment Comparison', icon: <BarChart3 className="h-4 w-4" /> },
      { id: 'audit-report', label: 'Audit Report', icon: <FileText className="h-4 w-4" /> },
    ]},
    { label: 'Loan Performance', reports: [
      { id: 'aging', label: 'Portfolio Aging Summary', icon: <AlertTriangle className="h-4 w-4" /> },
      { id: 'portfolio-aging-detailed', label: 'Portfolio Aging Detailed', icon: <AlertTriangle className="h-4 w-4" /> },
      { id: 'delinquent', label: 'Delinquent Accounts', icon: <AlertTriangle className="h-4 w-4" /> },
      { id: 'past-due', label: 'Past Due Analysis', icon: <AlertTriangle className="h-4 w-4" /> },
      { id: 'par', label: 'Portfolio-at-Risk', icon: <TrendingUp className="h-4 w-4" /> },
      { id: 'renewals', label: 'Renewal Analysis', icon: <TrendingUp className="h-4 w-4" /> },
    ]},
    { label: 'Collections', reports: [
      { id: 'collection', label: 'Collection Summary', icon: <Receipt className="h-4 w-4" /> },
      { id: 'daily-collection', label: 'Daily Collection', icon: <Receipt className="h-4 w-4" /> },
      { id: 'weekly-collection', label: 'Weekly Collection', icon: <Receipt className="h-4 w-4" /> },
      { id: 'monthly-receipts', label: 'Monthly Receipts', icon: <Receipt className="h-4 w-4" /> },
      { id: 'collection-logs', label: 'Collection Logs', icon: <Receipt className="h-4 w-4" /> },
      { id: 'collector-efficiency', label: 'Collector Efficiency', icon: <Users className="h-4 w-4" /> },
    ]},
    { label: 'Financial Statements', reports: [
      { id: 'income-statement', label: 'Income Statement', icon: <FileText className="h-4 w-4" /> },
      { id: 'comparative-pl', label: 'Comparative P&L', icon: <FileText className="h-4 w-4" /> },
      { id: 'balance-sheet', label: 'Balance Sheet', icon: <Landmark className="h-4 w-4" /> },
      { id: 'trial-balance', label: 'Trial Balance', icon: <ScrollText className="h-4 w-4" /> },
      { id: 'budget-vs-actual', label: 'Budget vs Actual', icon: <FileText className="h-4 w-4" /> },
      { id: 'cash-flow', label: 'Cash Flow', icon: <TrendingUp className="h-4 w-4" /> },
      { id: 'changes-in-equity', label: 'Changes in Equity', icon: <FileText className="h-4 w-4" /> },
      { id: 'notes-to-fs', label: 'Notes to FS', icon: <FileText className="h-4 w-4" /> },
      { id: 'annual-report', label: 'Annual Report', icon: <FileText className="h-4 w-4" /> },
    ]},
    { label: 'Member Equity', reports: [
      { id: 'share-capital', label: 'Share Capital', icon: <Banknote className="h-4 w-4" /> },
      { id: 'savings', label: 'Savings Report', icon: <PiggyBank className="h-4 w-4" /> },
      { id: 'cbu', label: 'Capital Build-Up (CBU)', icon: <Banknote className="h-4 w-4" /> },
      { id: 'soa-masterlist', label: 'SOA Masterlist', icon: <Banknote className="h-4 w-4" /> },
      { id: 'dividend', label: 'Dividend Projection', icon: <Banknote className="h-4 w-4" /> },
      { id: 'statutory-reserves', label: 'Statutory Reserves', icon: <Banknote className="h-4 w-4" /> },
      { id: 'surplus-allocation', label: 'Surplus Allocation', icon: <Banknote className="h-4 w-4" /> },
    ]},
    { label: 'Other', reports: [
      { id: 'disbursements', label: 'Disbursements', icon: <Receipt className="h-4 w-4" /> },
      { id: 'cash-advances', label: 'Cash Advances', icon: <Receipt className="h-4 w-4" /> },
      { id: 'creditors-group-life', label: 'CGL Insurance', icon: <FileText className="h-4 w-4" /> },
    ]},
  ]

  const d = data as any
  if (loading) return <div className="flex items-center justify-center py-16"><div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full" /></div>

  return (
    <div className="p-6">
      <style>{PRINT_STYLES}</style>
      <div className="mb-6 flex items-center gap-3">
        <Calendar className="h-5 w-5 text-gray-400" />
        <select value={period.month} onChange={e => setPeriod(p => ({ ...p, month: +e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          {Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{new Date(2024, i).toLocaleString('default', { month: 'long' })}</option>)}
        </select>
        <select value={period.year} onChange={e => setPeriod(p => ({ ...p, year: +e.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          {Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}
        </select>
      </div>
      <div className="flex gap-6">
        <div className="w-56 shrink-0">
          {categories.map(cat => (
            <div key={cat.label} className="mb-4">
              <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wider text-gray-400">{cat.label}</p>
              {cat.reports.map(r => (
                <button key={r.id} onClick={() => setActiveReport(r.id as ReportType)}
                  className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors ${activeReport === r.id ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                  {r.icon} {r.label}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <div className="mb-3 flex justify-end no-print">
            <button
              onClick={() => printReport(`${activeReport.replace(/-/g,' ')} — CoopERP`)}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
            >
              <Printer className="h-4 w-4" /> Print / Save PDF
            </button>
          </div>
          {renderReport()}
        </div>
      </div>
    </div>
  )

  function renderReport() {
    const ag = (bucket: string) => d.loans?.filter((l: any) => LoanService.computeAgingBucket(l.dpd ?? 0) === bucket) ?? []
    switch (activeReport) {
      case 'dashboard': return <Dashboard d={d} ag={ag} />
      case 'portfolio': return <PortfolioSummary d={d} />
      case 'aging': return <AgingReport d={d} ag={ag} />
      case 'delinquent': return <DelinquentReport d={d} />
      case 'par': return <ParReport d={d} ag={ag} />
      case 'income-statement': return <IncomeStatement d={d} />
      case 'comparative-pl': return <ComparativePL d={d} period={period} />
      case 'balance-sheet': return <BalanceSheet d={d} />
      case 'trial-balance': return <TrialBalance d={d} />
      case 'budget-vs-actual': return <BudgetVsActual d={d} />
      case 'share-capital': return <ShareCapitalReport d={d} />
      case 'savings': return <SavingsReport d={d} />
      case 'dividend': return <DividendReport d={d} />
      case 'statutory-reserves': return <StatutoryReserves d={d} />
      case 'surplus-allocation': return <SurplusAllocation d={d} />
      case 'membership': return <MembershipReport d={d} />
      case 'membership-growth': return <MembershipGrowth d={d} />
      case 'mfi-kpis': return <MfiKpis d={d} />
      case 'collection': case 'daily-collection': return <CollectionSummary d={d} />
      case 'collector-efficiency': return <CollectorEfficiency d={d} />
      case 'weekly-collection': case 'monthly-receipts': return <WeeklyCollection d={d} active={activeReport} />
      case 'collection-logs': return <CollectionLogs d={d} />
      case 'portfolio-aging-detailed': return <PortfolioAgingDetailed d={d} />
      case 'past-due': return <PastDue d={d} />
      case 'renewals': return <Renewals d={d} />
      case 'cash-flow': case 'disbursements': case 'cash-advances': return <CashFlow d={d} active={activeReport} />
      case 'segment-comparison': return <SegmentComparison d={d} />
      case 'changes-in-equity': return <ChangesInEquity d={d} />
      case 'annual-report': return <AnnualReport d={d} period={period} />
      case 'creditors-group-life': return <CGLReport d={d} />
      case 'soa-masterlist': return <SoaMasterlist d={d} />
      case 'cbu': return <CbuReport d={d} />
      case 'notes-to-fs': return <NotesToFS />
      case 'audit-report': return <AuditReport />
      default: return <Card><CardHeader title="Report" /><p className="py-8 text-center text-gray-400">Select a report from the sidebar.</p></Card>
    }
  }
}

// ─── Sub-components ────────────────────────────────────────

function KpiCard({ label, value, icon, color }: { label: string; value?: string; icon: React.ReactNode; color: string }) {
  const cm: Record<string, string> = { blue: 'bg-blue-50 text-blue-600', green: 'bg-green-50 text-green-600', red: 'bg-red-50 text-red-600', purple: 'bg-purple-50 text-purple-600', yellow: 'bg-yellow-50 text-yellow-600' }
  return <div className="rounded-xl border border-gray-200 bg-white p-4"><div className="flex items-center justify-between mb-2"><p className="text-xs text-gray-500">{label}</p><div className={`rounded-lg p-1.5 ${cm[color] ?? 'bg-gray-50'}`}>{icon}</div></div><p className="text-xl font-bold text-gray-900">{value ?? '—'}</p></div>
}

function Dashboard({ d, ag }: any) {
  return <div className="space-y-6">
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <KpiCard label="Total Members" value={d.totalMembers?.toLocaleString()} icon={<Users className="h-5 w-5" />} color="blue" />
      <KpiCard label="Active Loans" value={d.activeLoans?.length?.toLocaleString()} icon={<ScrollText className="h-5 w-5" />} color="purple" />
      <KpiCard label="Total Portfolio" value={`₱${(d.totalPortfolio ?? 0).toLocaleString()}`} icon={<Banknote className="h-5 w-5" />} color="green" />
      <KpiCard label="Collections" value={`₱${(d.totalPayments ?? 0).toLocaleString()}`} icon={<Receipt className="h-5 w-5" />} color="blue" />
    </div>
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
      <KpiCard label="Delinquent Loans" value={d.delinquentLoans?.length?.toLocaleString()} icon={<AlertTriangle className="h-5 w-5" />} color={d.delinquentLoans?.length > 0 ? 'red' : 'green'} />
      <KpiCard label="PAR Rate" value={`${(d.parRate ?? 0).toFixed(2)}%`} icon={<TrendingUp className="h-5 w-5" />} color={d.parRate > 5 ? 'red' : 'green'} />
      <KpiCard label="Avg Loan Size" value={`₱${(d.avgLoanSize ?? 0).toLocaleString()}`} icon={<BarChart3 className="h-5 w-5" />} color="purple" />
      <KpiCard label="Active Members" value={d.activeMembers?.length?.toLocaleString()} icon={<Users className="h-5 w-5" />} color="green" />
    </div>
    <Card><CardHeader title="Portfolio Status" />
      <div className="space-y-3">{['current','1-30','31-60','61-90','91-180','180+'].map(b => {
        const c = ag(b).length
        return <div key={b} className="flex items-center gap-3"><span className="w-20 text-sm text-gray-600">{b === 'current' ? 'Current' : `${b} days`}</span>
          <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${b === 'current' ? 'bg-green-500' : b === '180+' ? 'bg-red-600' : 'bg-yellow-500'}`} style={{ width: `${d.activeLoans?.length > 0 ? (c / d.activeLoans.length) * 100 : 0}%` }} /></div>
          <span className="w-16 text-right text-sm text-gray-700">{c}</span></div>
      })}</div>
    </Card>
  </div>
}

function PortfolioSummary({ d }: any) {
  const byProduct = new Map<string, { count: number; total: number }>()
  d.activeLoans?.forEach((l: any) => { const t = l.loanType ?? 'other'; if (!byProduct.has(t)) byProduct.set(t, { count: 0, total: 0 }); const r = byProduct.get(t)!; r.count++; r.total += (l.principalAmount ?? 0) })
  return <Card><CardHeader title="Portfolio Summary" description={`${d.activeLoans?.length ?? 0} active loans · ₱${(d.totalPortfolio ?? 0).toLocaleString()}`} />
    <table className="w-full text-sm"><thead><tr className="text-left text-gray-500"><th className="pb-2 pr-4">Product Type</th><th className="pb-2 pr-4 text-right">Count</th><th className="pb-2 pr-4 text-right">Principal</th><th className="pb-2 text-right">%</th></tr></thead>
    <tbody className="divide-y divide-gray-100">{Array.from(byProduct.entries()).map(([t, r]) =>
      <tr key={t}><td className="py-2 pr-4 capitalize">{t.replace(/_/g, ' ')}</td><td className="py-2 pr-4 text-right">{r.count}</td><td className="py-2 pr-4 text-right">₱{r.total.toLocaleString()}</td><td className="py-2 text-right">{d.totalPortfolio > 0 ? ((r.total / d.totalPortfolio) * 100).toFixed(1) : '0'}%</td></tr>
    )}</tbody></table></Card>
}

function AgingReport({ d, ag }: any) {
  return <Card><CardHeader title="Portfolio Aging Report" />
    <table className="w-full text-sm"><thead><tr className="text-left text-gray-500"><th className="pb-2 pr-4">Aging Bucket</th><th className="pb-2 pr-4 text-right">Count</th><th className="pb-2 pr-4 text-right">Total Principal</th><th className="pb-2 text-right">% of Portfolio</th></tr></thead>
    <tbody className="divide-y divide-gray-100">{[['current','Current'],['1-30','1-30 Days'],['31-60','31-60 Days'],['61-90','61-90 Days'],['91-180','91-180 Days'],['180+','Over 180 Days']].map(([k, l]) => {
      const bl = ag(k); const t = bl.reduce((s: number, x: any) => s + (x.principalAmount ?? 0), 0)
      return <tr key={k}><td className="py-2 pr-4">{l}</td><td className="py-2 pr-4 text-right">{bl.length}</td><td className="py-2 pr-4 text-right">₱{t.toLocaleString()}</td><td className="py-2 text-right">{d.totalPortfolio > 0 ? ((t / d.totalPortfolio) * 100).toFixed(1) : '0'}%</td></tr>
    })}</tbody></table></Card>
}

function DelinquentReport({ d }: any) {
  const dl = d.delinquentLoans ?? []
  return <Card><CardHeader title="Delinquent Accounts" description={`${dl.length} loan${dl.length !== 1 ? 's' : ''} past due`} />
    {dl.length === 0 ? <p className="py-8 text-center text-gray-400">No delinquent accounts</p> :
    <table className="w-full text-sm"><thead><tr className="text-left text-gray-500"><th className="pb-2 pr-4">Loan #</th><th className="pb-2 pr-4">Borrower</th><th className="pb-2 pr-4 text-right">Principal</th><th className="pb-2 pr-4 text-right">DPD</th><th className="pb-2 text-right">Aging</th></tr></thead>
    <tbody className="divide-y divide-gray-100">{dl.map((l: any) => <tr key={l.id}><td className="py-2 pr-4 font-mono text-sm">{l.loanNumber}</td><td className="py-2 pr-4">{d.lookups?.resolveBorrower(l.borrowerId) ?? l.borrowerId}</td><td className="py-2 pr-4 text-right">₱{(l.principalAmount ?? 0).toLocaleString()}</td><td className="py-2 pr-4 text-right text-red-600 font-medium">{l.dpd}d</td><td className="py-2 text-right">{LoanService.computeAgingBucket(l.dpd ?? 0)}</td></tr>)}</tbody></table>}</Card>
}

function ParReport({ d, ag }: any) {
  const pd = [['current','Current'],['1-30','1-30 Days'],['31-60','31-60 Days'],['61-90','61-90 Days'],['91-180','91-180 Days'],['180+','Over 180 Days']]
  return <Card><CardHeader title="Portfolio-at-Risk (PAR)" description={`Overall PAR: ${(d.parRate ?? 0).toFixed(2)}%`} />
    <table className="w-full text-sm"><thead><tr className="text-left text-gray-500"><th className="pb-2 pr-4">Bucket</th><th className="pb-2 pr-4 text-right">Count</th><th className="pb-2 text-right">% of Active</th></tr></thead>
    <tbody className="divide-y divide-gray-100">{pd.map(([k, l]) => { const c = ag(k).length; return <tr key={k}><td className="py-2 pr-4">{l}</td><td className="py-2 pr-4 text-right">{c}</td><td className="py-2 text-right">{d.activeLoans?.length > 0 ? ((c / d.activeLoans.length) * 100).toFixed(1) : '0'}%</td></tr>})}</tbody></table></Card>
}

function IncomeStatement({ d }: any) {
  const allLines = d.entries?.flatMap((e: any) => (e.lines ?? [])) ?? []
  const multi = ReportGenerator.generateMultiStepIncomeStatement(d.accounts ?? [], allLines)
  
  return (
    <Card>
      <CardHeader
        title="Multi-Step Income Statement (Profit & Loss)"
        description={`Net Surplus / Income: ₱${multi.netIncome.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`}
      />
      <div className="space-y-6">
        {/* Operating Revenue */}
        <div>
          <div className="flex justify-between border-b pb-1 mb-2">
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">1. Operating Revenue</h4>
            <span className="text-xs font-semibold text-gray-500">Amount (₱)</span>
          </div>
          {multi.operatingRevenue.length === 0 ? (
            <p className="text-xs text-gray-400 py-1">No operating revenue entries recorded</p>
          ) : (
            multi.operatingRevenue.map(x => (
              <div key={x.code} className="flex justify-between py-1 text-sm text-gray-700">
                <span className="font-mono text-xs text-gray-500 mr-2">{x.code}</span>
                <span className="flex-1">{x.name}</span>
                <span className="font-mono">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            ))
          )}
          <div className="flex justify-between border-t border-gray-200 pt-1 mt-1 font-semibold text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded">
            <span>Total Operating Revenue</span>
            <span className="font-mono text-emerald-700">₱{multi.totalOperatingRevenue.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Cost of Goods Sold */}
        <div>
          <div className="flex justify-between border-b pb-1 mb-2">
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">2. Cost of Goods Sold / Direct Costs</h4>
            <span className="text-xs font-semibold text-gray-500">Amount (₱)</span>
          </div>
          {multi.costOfGoodsSold.length === 0 ? (
            <p className="text-xs text-gray-400 py-1">No direct cost entries recorded</p>
          ) : (
            multi.costOfGoodsSold.map(x => (
              <div key={x.code} className="flex justify-between py-1 text-sm text-gray-700">
                <span className="font-mono text-xs text-gray-500 mr-2">{x.code}</span>
                <span className="flex-1">{x.name}</span>
                <span className="font-mono">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            ))
          )}
          <div className="flex justify-between border-t border-gray-200 pt-1 mt-1 font-semibold text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded">
            <span>Total Cost of Goods Sold</span>
            <span className="font-mono text-red-700">₱{multi.totalCostOfGoodsSold.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Gross Profit Callout */}
        <div className="flex justify-between border-y-2 border-gray-300 py-2 font-bold text-sm bg-slate-50 px-3 rounded">
          <span>GROSS PROFIT / MARGIN</span>
          <span className="font-mono text-blue-700">₱{multi.grossProfit.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
        </div>

        {/* Operating Expenses */}
        <div>
          <div className="flex justify-between border-b pb-1 mb-2">
            <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">3. Operating Expenses</h4>
            <span className="text-xs font-semibold text-gray-500">Amount (₱)</span>
          </div>
          {multi.operatingExpenses.length === 0 ? (
            <p className="text-xs text-gray-400 py-1">No operating expense entries recorded</p>
          ) : (
            multi.operatingExpenses.map(x => (
              <div key={x.code} className="flex justify-between py-1 text-sm text-gray-700">
                <span className="font-mono text-xs text-gray-500 mr-2">{x.code}</span>
                <span className="flex-1">{x.name}</span>
                <span className="font-mono">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            ))
          )}
          <div className="flex justify-between border-t border-gray-200 pt-1 mt-1 font-semibold text-sm text-gray-900 bg-gray-50 px-2 py-1 rounded">
            <span>Total Operating Expenses</span>
            <span className="font-mono text-red-700">₱{multi.totalOperatingExpenses.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Operating Income */}
        <div className="flex justify-between border-t border-gray-200 pt-2 text-sm font-semibold">
          <span>OPERATING INCOME</span>
          <span className="font-mono text-gray-900">₱{multi.operatingIncome.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
        </div>

        {/* Final Net Surplus */}
        <div className="flex justify-between border-t-2 border-gray-900 pt-3 text-lg font-bold bg-slate-100 p-3 rounded-lg">
          <span>NET SURPLUS / (NET INCOME)</span>
          <span className={`font-mono ${multi.netIncome >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            ₱{multi.netIncome.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    </Card>
  )
}

function BalanceSheet({ d }: any) {
  const allLines = d.entries?.flatMap((e: any) => (e.lines ?? [])) ?? []
  const classified = ReportGenerator.generateClassifiedBalanceSheet(d.accounts ?? [], allLines)

  return (
    <Card>
      <CardHeader
        title="Classified Balance Sheet"
        description={`As of ${new Date().toLocaleDateString()} · Assets = Liabilities + Equity (${classified.isBalanced ? 'Balanced' : 'Imbalance: ₱' + classified.imbalance.toFixed(2)})`}
      />
      <div className="space-y-6">
        {/* Balance Status Banner */}
        <div className={`p-3 rounded-lg text-sm font-medium flex justify-between items-center ${classified.isBalanced ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          <span>{classified.isBalanced ? '✅ Fundamental Accounting Equation Verified: Assets = Liabilities + Equity' : `⚠️ Balance Sheet Imbalance: ₱${classified.imbalance.toFixed(2)}`}</span>
          <span className="font-mono font-bold">₱{classified.totalAssets.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* ASSETS COLUMN */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-blue-700 border-b-2 border-blue-600 pb-1">
              Assets
            </h3>

            {/* Current Assets */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Current Assets</h4>
              {classified.currentAssets.map(x => (
                <div key={x.code} className="flex justify-between py-1 text-sm">
                  <span className="font-mono text-xs text-gray-400 mr-2">{x.code}</span>
                  <span className="flex-1 text-gray-800">{x.name}</span>
                  <span className="font-mono font-medium">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-1 font-semibold text-xs text-gray-700 bg-gray-50 p-1.5 rounded mt-1">
                <span>Total Current Assets</span>
                <span className="font-mono">₱{classified.totalCurrentAssets.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            {/* Non-Current Assets */}
            {classified.nonCurrentAssets.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Non-Current Assets</h4>
                {classified.nonCurrentAssets.map(x => (
                  <div key={x.code} className="flex justify-between py-1 text-sm">
                    <span className="font-mono text-xs text-gray-400 mr-2">{x.code}</span>
                    <span className="flex-1 text-gray-800">{x.name}</span>
                    <span className="font-mono font-medium">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t pt-1 font-semibold text-xs text-gray-700 bg-gray-50 p-1.5 rounded mt-1">
                  <span>Total Non-Current Assets</span>
                  <span className="font-mono">₱{classified.totalNonCurrentAssets.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}

            <div className="flex justify-between border-t-2 border-blue-700 pt-2 font-bold text-base bg-blue-50 p-2.5 rounded-lg text-blue-900">
              <span>TOTAL ASSETS</span>
              <span className="font-mono">₱{classified.totalAssets.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>

          {/* LIABILITIES & EQUITY COLUMN */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-wider text-purple-700 border-b-2 border-purple-600 pb-1">
              Liabilities & Equity
            </h3>

            {/* Current Liabilities */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Current Liabilities</h4>
              {classified.currentLiabilities.map(x => (
                <div key={x.code} className="flex justify-between py-1 text-sm">
                  <span className="font-mono text-xs text-gray-400 mr-2">{x.code}</span>
                  <span className="flex-1 text-gray-800">{x.name}</span>
                  <span className="font-mono font-medium">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-1 font-semibold text-xs text-gray-700 bg-gray-50 p-1.5 rounded mt-1">
                <span>Total Current Liabilities</span>
                <span className="font-mono">₱{classified.totalCurrentLiabilities.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            {/* Non-Current Liabilities */}
            {classified.nonCurrentLiabilities.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Long-Term Liabilities</h4>
                {classified.nonCurrentLiabilities.map(x => (
                  <div key={x.code} className="flex justify-between py-1 text-sm">
                    <span className="font-mono text-xs text-gray-400 mr-2">{x.code}</span>
                    <span className="flex-1 text-gray-800">{x.name}</span>
                    <span className="font-mono font-medium">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t pt-1 font-semibold text-xs text-gray-700 bg-gray-50 p-1.5 rounded mt-1">
                  <span>Total Long-Term Liabilities</span>
                  <span className="font-mono">₱{classified.totalNonCurrentLiabilities.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}

            {/* Equity */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Equity</h4>
              {classified.equity.map(x => (
                <div key={x.code} className="flex justify-between py-1 text-sm">
                  <span className="font-mono text-xs text-gray-400 mr-2">{x.code}</span>
                  <span className="flex-1 text-gray-800">{x.name}</span>
                  <span className="font-mono font-medium">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
                </div>
              ))}
              <div className="flex justify-between border-t pt-1 font-semibold text-xs text-gray-700 bg-gray-50 p-1.5 rounded mt-1">
                <span>Total Equity</span>
                <span className="font-mono">₱{classified.totalEquity.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            <div className="flex justify-between border-t-2 border-purple-700 pt-2 font-bold text-base bg-purple-50 p-2.5 rounded-lg text-purple-900">
              <span>TOTAL LIABILITIES & EQUITY</span>
              <span className="font-mono">₱{classified.totalLiabilitiesAndEquity.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}

function CashFlow({ d, active }: any) {
  const allLines = d.entries?.flatMap((e: any) => (e.lines ?? [])) ?? []
  const cf = ReportGenerator.generateCashFlowStatement(d.accounts ?? [], allLines, 0)

  return (
    <Card>
      <CardHeader
        title="Statement of Cash Flows"
        description={`Net Cash Flow: ₱${cf.netCashFlow.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`}
      />
      <div className="space-y-6">
        <div>
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider border-b pb-1 mb-2">
            1. Cash Flows from Operating Activities
          </h4>
          {cf.operatingActivities.map((x, idx) => (
            <div key={idx} className="flex justify-between py-1 text-sm text-gray-700">
              <span>{x.description}</span>
              <span className="font-mono">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
            </div>
          ))}
          <div className="flex justify-between border-t pt-1 mt-1 font-semibold text-sm bg-gray-50 p-2 rounded">
            <span>Net Cash from Operating Activities</span>
            <span className="font-mono">₱{cf.totalOperatingCashFlow.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        <div>
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider border-b pb-1 mb-2">
            2. Cash Flows from Investing Activities
          </h4>
          {cf.investingActivities.length === 0 ? (
            <p className="text-xs text-gray-400 py-1">No capital expenditure or investing transactions</p>
          ) : (
            cf.investingActivities.map((x, idx) => (
              <div key={idx} className="flex justify-between py-1 text-sm text-gray-700">
                <span>{x.description}</span>
                <span className="font-mono">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            ))
          )}
          <div className="flex justify-between border-t pt-1 mt-1 font-semibold text-sm bg-gray-50 p-2 rounded">
            <span>Net Cash from Investing Activities</span>
            <span className="font-mono">₱{cf.totalInvestingCashFlow.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        <div>
          <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider border-b pb-1 mb-2">
            3. Cash Flows from Financing Activities
          </h4>
          {cf.financingActivities.length === 0 ? (
            <p className="text-xs text-gray-400 py-1">No debt or equity financing transactions</p>
          ) : (
            cf.financingActivities.map((x, idx) => (
              <div key={idx} className="flex justify-between py-1 text-sm text-gray-700">
                <span>{x.description}</span>
                <span className="font-mono">₱{x.amount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
              </div>
            ))
          )}
          <div className="flex justify-between border-t pt-1 mt-1 font-semibold text-sm bg-gray-50 p-2 rounded">
            <span>Net Cash from Financing Activities</span>
            <span className="font-mono">₱{cf.totalFinancingCashFlow.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span>
          </div>
        </div>

        <div className="flex justify-between border-t-2 border-gray-900 pt-3 text-lg font-bold bg-slate-100 p-3 rounded-lg">
          <span>NET INCREASE / (DECREASE) IN CASH</span>
          <span className={`font-mono ${cf.netCashFlow >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            ₱{cf.netCashFlow.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
          </span>
        </div>
      </div>
    </Card>
  )
}

function TrialBalance({ d }: any) {
  const tb = ReportGenerator.generateTrialBalance(d.accounts ?? [], d.entries?.flatMap((e: any) => (e.lines ?? [])) ?? [])
  const td = tb.reduce((s: number, r: any) => s + r.debit, 0)
  const tc = tb.reduce((s: number, r: any) => s + r.credit, 0)
  return (
    <Card>
      <CardHeader title="Trial Balance" description={`${tb.length} accounts · Debits: ₱${td.toLocaleString()} · Credits: ₱${tc.toLocaleString()}`} />
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="pb-2 pr-2">Code</th>
              <th className="pb-2 pr-4">Account</th>
              <th className="pb-2 pr-2 text-right">Debit</th>
              <th className="pb-2 pr-2 text-right">Credit</th>
              <th className="pb-2 text-right">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tb.map((r: any) => (
              <tr key={r.code} className={r.balance !== 0 ? '' : 'opacity-50'}>
                <td className="py-1.5 pr-2 font-mono text-xs">{r.code}</td>
                <td className="py-1.5 pr-4">{r.name}</td>
                <td className="py-1.5 pr-2 text-right">{r.debit > 0 ? `₱${r.debit.toLocaleString()}` : ''}</td>
                <td className="py-1.5 pr-2 text-right">{r.credit > 0 ? `₱${r.credit.toLocaleString()}` : ''}</td>
                <td className="py-1.5 text-right font-medium">₱{Math.abs(r.balance).toLocaleString()} {r.balance >= 0 ? 'Dr' : 'Cr'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function ComparativePL({ d, period }: any) {
  const currentLines = d.entries?.filter((e: any) => new Date(e.transactionDate || e.entryDate || 0).getFullYear() === period.year).flatMap((e: any) => (e.lines ?? [])) ?? []
  const priorLines = d.entries?.filter((e: any) => new Date(e.transactionDate || e.entryDate || 0).getFullYear() === period.year - 1).flatMap((e: any) => (e.lines ?? [])) ?? []
  const multi = ReportGenerator.generateComparativeIncomeStatement(d.accounts ?? [], currentLines, d.accounts ?? [], priorLines, String(period.year), String(period.year - 1))
  
  return (
    <Card>
      <CardHeader title="Comparative Income Statement (YoY)" description={`Net Surplus: ₱${multi.netIncome.currentAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`} />
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="pb-2 pr-4">Code</th>
              <th className="pb-2 pr-4">Account</th>
              <th className="pb-2 pr-4 text-right">{multi.periodLabel} (₱)</th>
              <th className="pb-2 pr-4 text-right">{multi.priorLabel} (₱)</th>
              <th className="pb-2 pr-4 text-right">Change (₱)</th>
              <th className="pb-2 text-right">Change (%)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {multi.operatingRevenue.length > 0 && <tr><td colSpan={6} className="py-2 font-bold bg-gray-50 uppercase text-xs text-gray-700">1. Operating Revenue</td></tr>}
            {multi.operatingRevenue.map(x => (
              <tr key={x.code}>
                <td className="py-1.5 pr-4 font-mono text-xs">{x.code}</td>
                <td className="py-1.5 pr-4">{x.name}</td>
                <td className="py-1.5 pr-4 text-right">₱{x.currentAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className="py-1.5 pr-4 text-right">₱{x.priorAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className="py-1.5 pr-4 text-right">₱{x.changeAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className={`py-1.5 text-right ${x.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>{x.changePercent.toFixed(2)}%</td>
              </tr>
            ))}
            {multi.operatingRevenue.length > 0 && (
              <tr className="font-semibold bg-gray-50">
                <td colSpan={2} className="py-2 pr-4 text-right">Total Operating Revenue</td>
                <td className="py-2 pr-4 text-right text-emerald-700">₱{multi.totalOperatingRevenue.currentAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className="py-2 pr-4 text-right text-emerald-700">₱{multi.totalOperatingRevenue.priorAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className="py-2 pr-4 text-right text-emerald-700">₱{multi.totalOperatingRevenue.changeAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className={`py-2 text-right ${multi.totalOperatingRevenue.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>{multi.totalOperatingRevenue.changePercent.toFixed(2)}%</td>
              </tr>
            )}

            {multi.operatingExpenses.length > 0 && <tr><td colSpan={6} className="py-2 font-bold bg-gray-50 uppercase text-xs text-gray-700 mt-2">2. Operating Expenses</td></tr>}
            {multi.operatingExpenses.map(x => (
              <tr key={x.code}>
                <td className="py-1.5 pr-4 font-mono text-xs">{x.code}</td>
                <td className="py-1.5 pr-4">{x.name}</td>
                <td className="py-1.5 pr-4 text-right">₱{x.currentAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className="py-1.5 pr-4 text-right">₱{x.priorAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className="py-1.5 pr-4 text-right">₱{x.changeAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className={`py-1.5 text-right ${x.changePercent <= 0 ? 'text-green-600' : 'text-red-600'}`}>{x.changePercent.toFixed(2)}%</td>
              </tr>
            ))}
            {multi.operatingExpenses.length > 0 && (
              <tr className="font-semibold bg-gray-50">
                <td colSpan={2} className="py-2 pr-4 text-right">Total Operating Expenses</td>
                <td className="py-2 pr-4 text-right text-red-700">₱{multi.totalOperatingExpenses.currentAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className="py-2 pr-4 text-right text-red-700">₱{multi.totalOperatingExpenses.priorAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className="py-2 pr-4 text-right text-red-700">₱{multi.totalOperatingExpenses.changeAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className={`py-2 text-right ${multi.totalOperatingExpenses.changePercent <= 0 ? 'text-green-600' : 'text-red-600'}`}>{multi.totalOperatingExpenses.changePercent.toFixed(2)}%</td>
              </tr>
            )}

            <tr className="font-bold bg-slate-100 text-lg border-t-2 border-gray-900">
              <td colSpan={2} className="py-3 pr-4">NET SURPLUS / (NET INCOME)</td>
              <td className={`py-3 pr-4 text-right ${multi.netIncome.currentAmount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>₱{multi.netIncome.currentAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
              <td className={`py-3 pr-4 text-right ${multi.netIncome.priorAmount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>₱{multi.netIncome.priorAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
              <td className={`py-3 pr-4 text-right ${multi.netIncome.changeAmount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>₱{multi.netIncome.changeAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
              <td className={`py-3 text-right ${multi.netIncome.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>{multi.netIncome.changePercent.toFixed(2)}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function BudgetVsActual({ d }: any) {
  const allLines = d.entries?.flatMap((e: any) => (e.lines ?? [])) ?? []
  
  // Create a realistic dummy budget based on accounts
  const budget = (d.accounts ?? []).map((a: any) => ({
    accountCode: a.code,
    budgetAmount: a.accountType === 'income' ? 100000 : a.accountType === 'expense' ? 50000 : 0
  })).filter((b: any) => b.budgetAmount > 0)

  const variance = ReportGenerator.generateBudgetVsActual(budget, d.accounts ?? [], allLines)

  return (
    <Card>
      <CardHeader title="Budget vs Actual Variance Report" description="Compare current period actuals with budgeted targets" />
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="pb-2 pr-4">Code</th>
              <th className="pb-2 pr-4">Account</th>
              <th className="pb-2 pr-4 text-right">Budget (₱)</th>
              <th className="pb-2 pr-4 text-right">Actual (₱)</th>
              <th className="pb-2 pr-4 text-right">Variance (₱)</th>
              <th className="pb-2 text-right">Var %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {variance.map(r => (
              <tr key={r.accountCode}>
                <td className="py-2 pr-4 font-mono text-xs">{r.accountCode}</td>
                <td className="py-2 pr-4">{r.name}</td>
                <td className="py-2 pr-4 text-right">₱{r.budget.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className="py-2 pr-4 text-right">₱{r.actual.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</td>
                <td className={`py-2 pr-4 text-right font-medium ${r.favorable ? 'text-green-600' : 'text-red-600'}`}>
                  {r.variance > 0 ? '+' : ''}₱{r.variance.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                </td>
                <td className={`py-2 text-right font-medium ${r.favorable ? 'text-green-600' : 'text-red-600'}`}>
                  {r.variancePercent > 0 ? '+' : ''}{r.variancePercent.toFixed(2)}%
                </td>
              </tr>
            ))}
            {variance.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-gray-400">No budget data available</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function ShareCapitalReport({ d }: any) {
  const sc = d.members?.reduce((s: number, m: any) => s + (m.shareCapitalBalance ?? m.totalShareCapital ?? 0), 0) ?? 0
  return (
    <Card>
      <CardHeader title="Share Capital Report" description={`₱${sc.toLocaleString()} total share capital`} />
      <div className="grid grid-cols-3 gap-4">
        <KpiCard label="Total Share Capital" value={`₱${sc.toLocaleString()}`} icon={<Banknote className="h-5 w-5" />} color="green" />
        <KpiCard label="Members with Shares" value={String(d.members?.filter((m: any) => (m.shareCapitalBalance ?? 0) > 0).length ?? 0)} icon={<Users className="h-5 w-5" />} color="blue" />
        <KpiCard label="Avg per Member" value={`₱${d.members?.length > 0 ? (sc / d.members.length).toLocaleString() : '0'}`} icon={<BarChart3 className="h-5 w-5" />} color="purple" />
      </div>
    </Card>
  )
}

function SavingsReport({ d }: any) {
  const ts = d.members?.reduce((s: number, m: any) => s + (m.savingsBalance ?? 0), 0) ?? 0
  const sv = d.members?.filter((m: any) => (m.savingsBalance ?? 0) > 0).length ?? 0
  return (
    <Card>
      <CardHeader title="Savings Report" />
      <div className="grid grid-cols-3 gap-4">
        <KpiCard label="Total Savings" value={`₱${ts.toLocaleString()}`} icon={<PiggyBank className="h-5 w-5" />} color="green" />
        <KpiCard label="Savers" value={String(sv)} icon={<Users className="h-5 w-5" />} color="blue" />
        <KpiCard label="Avg per Saver" value={`₱${sv > 0 ? (ts / sv).toLocaleString() : '0'}`} icon={<BarChart3 className="h-5 w-5" />} color="purple" />
      </div>
    </Card>
  )
}

function DividendReport({ d }: any) {
  const s = Math.max(0, d.entries?.reduce((s: number, e: any) => s + (e.totalCredit ?? 0) - (e.totalDebit ?? 0), 0) ?? 0)
  const st = ReportGenerator.computeStatutoryAllocations(s)
  const tst = st.reduce((ss: number, a: any) => ss + a.amount, 0)
  const dist = ReportGenerator.computeBenefitDistribution(s, tst, 50000, 500000, 100000)
  return (
    <Card>
      <CardHeader title="Dividend Projection" description={`Based on net surplus: ₱${s.toLocaleString()}`} />
      <div className="space-y-3">
        <div className="rounded-lg bg-green-50 p-3">
          <p className="text-xs text-green-600 mb-1">Patronage Refund Rate</p>
          <p className="text-xl font-bold text-green-700">{(dist.patronageRefundRate * 100).toFixed(2)}%</p>
          <p className="text-xs text-green-500">Pool: ₱{dist.patronageRefundPool.toLocaleString()}</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-3">
          <p className="text-xs text-blue-600 mb-1">Interest on Capital Rate</p>
          <p className="text-xl font-bold text-blue-700">{dist.interestRate.toFixed(2)}%</p>
          <p className="text-xs text-blue-500">Pool: ₱{dist.interestOnCapitalPool.toLocaleString()}</p>
        </div>
      </div>
    </Card>
  )
}

function StatutoryReserves({ d }: any) {
  const s = d.entries?.reduce((s: number, e: any) => s + (e.totalCredit ?? 0) - (e.totalDebit ?? 0), 0) ?? 0
  const al = ReportGenerator.computeStatutoryAllocations(Math.max(0, s))
  return (
    <Card>
      <CardHeader title="Statutory Fund Reserves" description="CDA-mandated fund allocations" />
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="pb-2 pr-4">Fund Type</th>
            <th className="pb-2 pr-4 text-right">%</th>
            <th className="pb-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {al.map((a: any) => (
            <tr key={a.fundType}>
              <td className="py-2 pr-4">{a.fundType}</td>
              <td className="py-2 pr-4 text-right">{a.percentage}%</td>
              <td className="py-2 text-right font-medium">₱{a.amount.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-semibold">
            <td className="pt-2 pr-4">Total</td>
            <td className="pt-2 pr-4 text-right">{al.reduce((ss: number, a: any) => ss + a.percentage, 0)}%</td>
            <td className="pt-2 text-right">₱{al.reduce((ss: number, a: any) => ss + a.amount, 0).toLocaleString()}</td>
          </tr>
        </tfoot>
      </table>
    </Card>
  )
}

function SurplusAllocation({ d }: any) {
  const s = Math.max(0, d.entries?.reduce((ss: number, e: any) => ss + (e.totalCredit ?? 0) - (e.totalDebit ?? 0), 0) ?? 0)
  const al = ReportGenerator.computeStatutoryAllocations(s)
  const ts = al.reduce((ss: number, a: any) => ss + a.amount, 0)
  const dist = ReportGenerator.computeBenefitDistribution(s, ts, 50000, 500000, 100000)
  return (
    <Card>
      <CardHeader title="Surplus Allocation" description={`Net Surplus: ₱${s.toLocaleString()}`} />
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Statutory Funds ({al.reduce((ss: number, a: any) => ss + a.percentage, 0)}%)</h4>
          {al.map((a: any) => (
            <div key={a.fundType} className="flex justify-between py-1 text-sm">
              <span>{a.fundType} ({a.percentage}%)</span>
              <span>₱{a.amount.toLocaleString()}</span>
            </div>
          ))}
          <div className="flex justify-between border-t pt-1 font-semibold">
            <span>Total Statutory</span>
            <span>₱{ts.toLocaleString()}</span>
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">Benefit Distribution</h4>
          <div className="flex justify-between py-1 text-sm">
            <span>Patronage Refund Pool (70%)</span>
            <span>₱{dist.patronageRefundPool.toLocaleString()}</span>
          </div>
          <div className="flex justify-between py-1 text-sm">
            <span>Interest on Capital Pool (30%)</span>
            <span>₱{dist.interestOnCapitalPool.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </Card>
  )
}

function MembershipReport({ d }: any) {
  const a = d.members?.filter((m: any) => m.membershipStatus === 'active').length ?? 0
  const i = d.members?.filter((m: any) => m.membershipStatus === 'inactive').length ?? 0
  const t = d.members?.filter((m: any) => m.membershipStatus === 'terminated').length ?? 0
  return (
    <Card>
      <CardHeader title="Membership Report" description={`${d.totalMembers ?? 0} total members`} />
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center">
          <p className="text-2xl font-bold text-green-700">{a}</p>
          <p className="text-xs text-green-600">Active</p>
        </div>
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-center">
          <p className="text-2xl font-bold text-yellow-700">{i}</p>
          <p className="text-xs text-yellow-600">Inactive</p>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
          <p className="text-2xl font-bold text-red-700">{t}</p>
          <p className="text-xs text-red-600">Terminated</p>
        </div>
      </div>
    </Card>
  )
}

function MembershipGrowth({ d }: any) {
  const a = d.members?.filter((m: any) => m.membershipStatus === 'active').length ?? 0
  const n = (d.totalMembers ?? 0) - a
  return (
    <Card>
      <CardHeader title="Membership Growth" description={`${d.totalMembers ?? 0} total members`} />
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-green-50 p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{a}</p>
          <p className="text-xs text-green-600">Active</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{n}</p>
          <p className="text-xs text-blue-600">Inactive/Terminated</p>
        </div>
      </div>
    </Card>
  )
}

function MfiKpis({ d }: any) {
  const nb = new Set(d.loans?.map((l: any) => l.borrowerId) ?? []).size
  return (
    <Card>
      <CardHeader title="MFI KPIs" />
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg p-3 text-center bg-blue-50">
          <p className="text-lg font-bold text-blue-700">{nb}</p>
          <p className="text-xs text-blue-600">Active Borrowers</p>
        </div>
        <div className="rounded-lg p-3 text-center bg-green-50">
          <p className="text-lg font-bold text-green-700">₱{(d.totalPortfolio ?? 0).toLocaleString()}</p>
          <p className="text-xs text-green-600">Gross Loan Portfolio</p>
        </div>
        <div className="rounded-lg p-3 text-center bg-purple-50">
          <p className="text-lg font-bold text-purple-700">{(d.parRate ?? 0).toFixed(2)}%</p>
          <p className="text-xs text-purple-600">PAR Rate</p>
        </div>
        <div className="rounded-lg p-3 text-center bg-yellow-50">
          <p className="text-lg font-bold text-yellow-700">{d.delinquentLoans?.length ?? 0}</p>
          <p className="text-xs text-yellow-600">Delinquent Borrowers</p>
        </div>
      </div>
    </Card>
  )
}

function CollectionSummary({ d }: any) {
  const tc = d.payments?.reduce((s: number, p: any) => s + (p.amount ?? 0), 0) ?? 0
  return (
    <Card>
      <CardHeader title="Collection Summary" />
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-green-50 p-4 text-center">
          <p className="text-2xl font-bold text-green-700">₱{tc.toLocaleString()}</p>
          <p className="text-xs text-green-600">Total Collected</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{d.payments?.length ?? 0}</p>
          <p className="text-xs text-blue-600">Transactions</p>
        </div>
      </div>
    </Card>
  )
}

function CollectorEfficiency({ d }: any) {
  const tc = d.payments?.reduce((s: number, p: any) => s + (p.amount ?? 0), 0) ?? 0
  return (
    <Card>
      <CardHeader title="Collector Efficiency" />
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-green-50 p-4 text-center">
          <p className="text-2xl font-bold text-green-700">₱{tc.toLocaleString()}</p>
          <p className="text-xs text-green-600">Total Collected</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{d.payments?.length ?? 0}</p>
          <p className="text-xs text-blue-600">Transactions</p>
        </div>
      </div>
    </Card>
  )
}

function WeeklyCollection({ d, active }: any) {
  const wp = d.payments?.filter((p: any) => p.paymentDate > Date.now() - 7 * 86400000) ?? []
  const wt = wp.reduce((s: number, p: any) => s + (p.amount ?? 0), 0)
  return (
    <Card>
      <CardHeader title={active === 'weekly-collection' ? 'Weekly Collection' : 'Monthly Receipts'} />
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-green-50 p-4 text-center">
          <p className="text-2xl font-bold text-green-700">₱{wt.toLocaleString()}</p>
          <p className="text-xs text-green-600">Total</p>
        </div>
        <div className="rounded-lg bg-blue-50 p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{wp.length}</p>
          <p className="text-xs text-blue-600">Transactions</p>
        </div>
      </div>
    </Card>
  )
}

function CollectionLogs({ d }: any) {
  const cl = d.payments?.slice(0, 100) ?? []
  const ct = cl.reduce((s: number, p: any) => s + (p.amount ?? 0), 0)
  return (
    <Card>
      <CardHeader title="Collection Logs" description={`${cl.length} recent transactions · ₱${ct.toLocaleString()}`} />
      <div className="max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500">
              <th className="pb-2 pr-2">Date</th>
              <th className="pb-2 pr-2">Receipt #</th>
              <th className="pb-2 pr-4">Borrower</th>
              <th className="pb-2 pr-2 text-right">Type</th>
              <th className="pb-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {cl.map((p: any) => (
              <tr key={p.id}>
                <td className="py-1.5 pr-2">{new Date(p.paymentDate).toLocaleDateString()}</td>
                <td className="py-1.5 pr-2">{p.receiptNumber || '—'}</td>
                <td className="py-1.5 pr-4">{d.lookups?.resolveBorrower(p.borrowerId) || p.borrowerId || '—'}</td>
                <td className="py-1.5 pr-2">{p.paymentType ?? 'regular'}</td>
                <td className="py-1.5 text-right font-medium">₱{(p.amount ?? 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function PortfolioAgingDetailed({ d }: any) {
  return (
    <Card>
      <CardHeader title="Portfolio Aging — Detailed" description="Per-loan aging breakdown" />
      {d.loans?.filter((l: any) => (l.dpd ?? 0) > 0).length === 0 ? (
        <p className="py-8 text-center text-gray-400">No past due loans</p>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pb-2 pr-2">Loan #</th>
                <th className="pb-2 pr-2">Borrower</th>
                <th className="pb-2 pr-2 text-right">Principal</th>
                <th className="pb-2 pr-2 text-right">DPD</th>
                <th className="pb-2">Bucket</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {d.loans
                ?.filter((l: any) => (l.dpd ?? 0) > 0)
                .sort((a: any, b: any) => (b.dpd ?? 0) - (a.dpd ?? 0))
                .slice(0, 100)
                .map((l: any) => (
                  <tr key={l.id}>
                    <td className="py-1.5 pr-2 font-mono text-xs">{l.loanNumber}</td>
                    <td className="py-1.5 pr-2">{d.lookups?.resolveBorrower(l.borrowerId) ?? l.borrowerId}</td>
                    <td className="py-1.5 pr-2 text-right">₱{(l.principalAmount ?? 0).toLocaleString()}</td>
                    <td className="py-1.5 pr-2 text-right text-red-600">{l.dpd}d</td>
                    <td className="py-1.5">{LoanService.computeAgingBucket(l.dpd ?? 0)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

function PastDue({ d }: any) {
  const pd = d.loans?.filter((l: any) => (l.dpd ?? 0) > 0) ?? []
  const pt = pd.reduce((s: number, l: any) => s + (l.principalAmount ?? 0), 0)
  return (
    <Card>
      <CardHeader title="Past Due Analysis" description={`${pd.length} loans · ₱${pt.toLocaleString()}`} />
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-red-50 p-3 text-center">
          <p className="text-2xl font-bold text-red-700">{pd.length}</p>
          <p className="text-xs text-red-600">Past Due Loans</p>
        </div>
        <div className="rounded-lg bg-red-50 p-3 text-center">
          <p className="text-2xl font-bold text-red-700">₱{pt.toLocaleString()}</p>
          <p className="text-xs text-red-600">Total Amount</p>
        </div>
      </div>
    </Card>
  )
}

function Renewals({ d }: any) {
  const rl = d.loans?.filter((l: any) => l.isReloan) ?? []
  return (
    <Card>
      <CardHeader title="Renewal Analysis" description={`${rl.length} renewed loans`} />
      <p className="py-8 text-center text-gray-400">{rl.length} loans flagged as renewals (isReloan).</p>
    </Card>
  )
}
function SegmentComparison({ d }: any) {
  const seg = new Map<string, { loans: number; amount: number; del: number }>()
  d.loans?.forEach((l: any) => {
    const t = l.loanType ?? 'other'
    if (!seg.has(t)) seg.set(t, { loans: 0, amount: 0, del: 0 })
    const r = seg.get(t)!
    r.loans++
    r.amount += (l.principalAmount ?? 0)
    if (l.isDelinquent) r.del++
  })
  return (
    <Card>
      <CardHeader title="Segment Comparison" description={`${seg.size} product segments`} />
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500">
            <th className="pb-2 pr-4">Segment</th>
            <th className="pb-2 pr-2 text-right">Loans</th>
            <th className="pb-2 pr-2 text-right">Portfolio</th>
            <th className="pb-2 pr-2 text-right">PAR</th>
            <th className="pb-2 text-right">Avg Size</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {Array.from(seg.entries()).map(([t, s]) => (
            <tr key={t}>
              <td className="py-2 pr-4 capitalize">{t.replace(/_/g, ' ')}</td>
              <td className="py-2 pr-2 text-right">{s.loans}</td>
              <td className="py-2 pr-2 text-right">₱{s.amount.toLocaleString()}</td>
              <td className="py-2 pr-2 text-right">{s.loans > 0 ? ((s.del / s.loans) * 100).toFixed(1) : '0'}%</td>
              <td className="py-2 text-right">₱{s.loans > 0 ? Math.round(s.amount / s.loans).toLocaleString() : '0'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  )
}
function ChangesInEquity({ d }: any) { const ea = d.accounts?.filter((a: any) => a.accountType === 'equity') ?? []; const et = ea.reduce((s: number, a: any) => s + (a.balance ?? a.startingBalance ?? 0), 0); return <Card><CardHeader title="Changes in Equity" description={`${ea.length} equity accounts`} /><table className="w-full text-sm"><thead><tr className="text-left text-gray-500"><th className="pb-2 pr-4">Account</th><th className="pb-2 pr-2 text-right">Balance</th><th className="pb-2 text-right">%</th></tr></thead><tbody className="divide-y divide-gray-100">{ea.map((a: any) => <tr key={a.code}><td className="py-2 pr-4">{a.code} - {a.name}</td><td className="py-2 pr-2 text-right">₱{(a.balance ?? a.startingBalance ?? 0).toLocaleString()}</td><td className="py-2 text-right">{et > 0 ? (((a.balance ?? a.startingBalance ?? 0) / et) * 100).toFixed(1) : '0'}%</td></tr>)}</tbody><tfoot><tr className="font-semibold"><td className="pt-2 pr-4">Total Equity</td><td className="pt-2 pr-2 text-right">₱{et.toLocaleString()}</td><td className="pt-2 text-right">100%</td></tr></tfoot></table></Card> }
function AnnualReport({ d, period }: any) { const ta = d.accounts?.filter((a: any) => a.accountType === 'asset').reduce((s: number, a: any) => s + (a.balance ?? a.startingBalance ?? 0), 0) ?? 0; const tl = d.accounts?.filter((a: any) => a.accountType === 'liability').reduce((s: number, a: any) => s + (a.balance ?? a.startingBalance ?? 0), 0) ?? 0; return <Card><CardHeader title="Annual Report" description={`Fiscal Year ${period.year}`} /><div className="grid grid-cols-3 gap-4 mb-6"><div className="rounded-lg bg-blue-50 p-4"><p className="text-xs text-blue-600 mb-1">Total Assets</p><p className="text-xl font-bold text-blue-700">₱{ta.toLocaleString()}</p></div><div className="rounded-lg bg-red-50 p-4"><p className="text-xs text-red-600 mb-1">Total Liabilities</p><p className="text-xl font-bold text-red-700">₱{tl.toLocaleString()}</p></div><div className="rounded-lg bg-green-50 p-4"><p className="text-xs text-green-600 mb-1">Total Equity</p><p className="text-xl font-bold text-green-700">₱{(ta - tl).toLocaleString()}</p></div></div><div className="grid grid-cols-2 gap-4"><div className="rounded-lg bg-purple-50 p-3"><p className="text-xs text-purple-600 mb-1">Members</p><p className="text-lg font-bold text-purple-700">{d.totalMembers ?? 0}</p></div><div className="rounded-lg bg-yellow-50 p-3"><p className="text-xs text-yellow-600 mb-1">Active Loans</p><p className="text-lg font-bold text-yellow-700">{d.activeLoans?.length ?? 0}</p></div><div className="rounded-lg bg-green-50 p-3"><p className="text-xs text-green-600 mb-1">Loan Portfolio</p><p className="text-lg font-bold text-green-700">₱{(d.totalPortfolio ?? 0).toLocaleString()}</p></div><div className="rounded-lg bg-blue-50 p-3"><p className="text-xs text-blue-600 mb-1">PAR Rate</p><p className="text-lg font-bold text-blue-700">{(d.parRate ?? 0).toFixed(2)}%</p></div></div></Card> }
function CGLReport({ d }: any) { const ib = new Set(d.loans?.map((l: any) => l.borrowerId) ?? []).size; const tc = d.activeLoans?.reduce((s: number, l: any) => s + (l.principalAmount ?? 0), 0) ?? 0; return <Card><CardHeader title="Creditors Group Life Insurance" /><div className="grid grid-cols-2 gap-4"><div className="rounded-lg bg-blue-50 p-4 text-center"><p className="text-2xl font-bold text-blue-700">{ib}</p><p className="text-xs text-blue-600">Insured Borrowers</p></div><div className="rounded-lg bg-green-50 p-4 text-center"><p className="text-2xl font-bold text-green-700">₱{tc.toLocaleString()}</p><p className="text-xs text-green-600">Total Coverage</p></div></div></Card> }
function SoaMasterlist({ d }: any) { return <Card><CardHeader title="SOA Masterlist" description="Statement of Accounts" /><p className="py-8 text-center text-gray-400">Total Portfolio: ₱{(d.totalPortfolio ?? 0).toLocaleString()} across {d.loans?.length ?? 0} loans.</p></Card> }
function CbuReport({ d }: any) { const tsc = d.loans?.reduce((s: number, l: any) => s + (l.savingsPerPayment ?? 0), 0) ?? 0; return <Card><CardHeader title="Capital Build-Up (CBU)" /><div className="rounded-lg bg-blue-50 p-4 text-center"><p className="text-2xl font-bold text-blue-700">₱{tsc.toLocaleString()}</p><p className="text-xs text-blue-600">Total CBU Collections</p></div></Card> }
function NotesToFS() { return <Card><CardHeader title="Notes to Financial Statements" /><div className="space-y-3 text-sm text-gray-600"><p><strong>1. Reporting Entity</strong> — The Cooperative is a CDA-registered entity operating under RA 9520.</p><p><strong>2. Basis of Preparation</strong> — Financial statements prepared under PFRS for SMEs.</p><p><strong>3. Revenue Recognition</strong> — Interest income recognized using effective interest method.</p><p><strong>4. Allowance for Probable Losses</strong> — Based on aging and portfolio-at-risk analysis.</p></div></Card> }
function AuditReport() { return <Card><CardHeader title="Audit Report" /><div className="space-y-3 text-sm text-gray-600"><p><strong>Independent Auditor's Report</strong></p><p>To the Board of Directors and Members of the Cooperative:</p><p>We have audited the accompanying financial statements of the Cooperative...</p></div></Card> }
